import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import './AdminPayments.css'
import LoadingScreen from '../components/LoadingScreen'
import alert from '../utils/alertService'

const AdminPayments = () => {
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const history = useHistory()

  useEffect(() => {
    fetchPayments()
  }, [])

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/admin/payments', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setPayments(data.payments || [])
      } else {
        setError('Failed to load payments')
      }
    } catch (err) {
      setError('Network error')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateStatus = async (paymentId, newStatus) => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/admin/payments/${paymentId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      })

      if (response.ok) {
        setPayments(payments.map(payment =>
          payment.id === paymentId ? { ...payment, status: newStatus } : payment
        ))
      } else {
        alert.error('Failed to update payment status')
      }
    } catch (err) {
      alert.error('Network error')
    }
  }

  const getFilteredPayments = () => {
    if (statusFilter === 'all') return payments
    return payments.filter(payment => payment.status === statusFilter)
  }

  const getStatusBadgeClass = (status) => {
    const classes = {
      completed: 'status-completed',
      pending: 'status-pending',
      failed: 'status-failed',
      refunded: 'status-refunded'
    }
    return classes[status] || 'status-default'
  }

  const calculateTotalRevenue = () => {
    return payments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
      .toFixed(2)
  }

  if (loading) {
    return <LoadingScreen message="Loading Payments" />
  }

  const filteredPayments = getFilteredPayments()

  return (
    <div className="admin-payments-container">
      <header className="page-header">
        <div>
          <h1>Payment Management</h1>
          <p className="subtitle">Track and manage all payment transactions</p>
        </div>
        <button onClick={() => history.push('/admin-dashboard')} className="back-button">
          Back to Dashboard
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="payments-stats">
        <div className="payments-stat-card payments-stat-card--success">
          <span className="payments-stat-icon">💰</span>
          <div>
            <span className="payments-stat-number">${calculateTotalRevenue()}</span>
            <span className="payments-stat-label">Total Revenue</span>
          </div>
        </div>
        <div className="payments-stat-card">
          <span className="payments-stat-icon">✅</span>
          <div>
            <span className="payments-stat-number">
              {payments.filter(p => p.status === 'completed').length}
            </span>
            <span className="payments-stat-label">Completed</span>
          </div>
        </div>
        <div className="payments-stat-card payments-stat-card--warning">
          <span className="payments-stat-icon">⏳</span>
          <div>
            <span className="payments-stat-number">
              {payments.filter(p => p.status === 'pending').length}
            </span>
            <span className="payments-stat-label">Pending</span>
          </div>
        </div>
        <div className="payments-stat-card payments-stat-card--error">
          <span className="payments-stat-icon">❌</span>
          <div>
            <span className="payments-stat-number">
              {payments.filter(p => p.status === 'failed').length}
            </span>
            <span className="payments-stat-label">Failed</span>
          </div>
        </div>
      </div>

      <div className="admin-payments-content">
        <div className="filter-section">
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All ({payments.length})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'completed' ? 'active' : ''}`}
            onClick={() => setStatusFilter('completed')}
          >
            Completed ({payments.filter(p => p.status === 'completed').length})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            Pending ({payments.filter(p => p.status === 'pending').length})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'failed' ? 'active' : ''}`}
            onClick={() => setStatusFilter('failed')}
          >
            Failed ({payments.filter(p => p.status === 'failed').length})
          </button>
        </div>

        <div className="payments-table-container">
          {filteredPayments.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">💳</div>
              <h3>No Payments Found</h3>
              <p>
                {statusFilter === 'all' 
                  ? 'No payments have been recorded yet. Payments will appear here once students make transactions.'
                  : `No ${statusFilter} payments found. Try selecting a different filter.`}
              </p>
            </div>
          ) : (
            <table className="payments-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Student</th>
                  <th>Test</th>
                  <th>Amount</th>
                  <th>Payment Method</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="transaction-id">#{payment.transaction_id || payment.id}</td>
                    <td>{payment.student_name}</td>
                    <td>{payment.test_title}</td>
                    <td className="amount">{payment.currency || 'PKR'} {parseFloat(payment.amount || 0).toFixed(2)}</td>
                    <td>{payment.payment_method || 'N/A'}</td>
                    <td>
                      <select
                        value={payment.status}
                        onChange={(e) => handleUpdateStatus(payment.id, e.target.value)}
                        className={`status-select ${getStatusBadgeClass(payment.status)}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    </td>
                    <td>{new Date(payment.created_at).toLocaleString()}</td>
                    <td>
                      <button className="view-details-btn">Details</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminPayments
